import { Link } from "react-router-dom";
import {
  invoiceLineClassificationLabels,
  type InvoiceLineClassification,
  type ParsedInvoiceDraft,
  type ParsedInvoiceLine,
} from "./types";
import type { SaveState } from "./InvoiceImportProvider";
import { getImportReviewState, type ImportReviewState } from "./importReviewState";
import {
  applyLineReviewUpdate,
  CUSTOM_INTERPRETED_UNIT,
  detectedUnitLabel,
  INTERPRETED_UNIT_PRESETS,
  reviewedUnitLabel,
  showReviewedQuantityField,
  unitSelectValue,
} from "./lineReview";

type InvoiceImportReviewFormProps = {
  draft: ParsedInvoiceDraft;
  readOnly?: boolean;
  onDraftChange: (draft: ParsedInvoiceDraft) => void;
  onLineChange: (lineId: string, patch: Partial<ParsedInvoiceLine>) => void;
  markedReady: boolean;
  reviewState: ImportReviewState;
  isMarkingReady: boolean;
  onMarkReady: () => void;
  importedEventId?: string;
  validationErrors: string[];
  actionError?: string | null;
  conflictEventId?: string | null;
  saveState?: SaveState;
  saveError?: string | null;
  isCreating: boolean;
  onCreateEvent: () => void;
};

export function InvoiceImportReviewForm({
  draft,
  readOnly = false,
  onDraftChange,
  onLineChange,
  markedReady,
  reviewState,
  isMarkingReady,
  onMarkReady,
  importedEventId,
  validationErrors,
  actionError,
  conflictEventId,
  isCreating,
  onCreateEvent,
}: InvoiceImportReviewFormProps) {
  const reviewCount = reviewState.unresolvedReviewCount;
  const { canCreateEvent, canMarkReady, createBlockingReasons } = reviewState;

  function updateDraftField(field: keyof ParsedInvoiceDraft, value: string) {
    if (readOnly) return;
    onDraftChange({ ...draft, [field]: value });
  }

  function updateLine(lineId: string, patch: Partial<ParsedInvoiceLine>) {
    if (readOnly) return;
    onLineChange(lineId, patch);
  }

  function handleUnitSelect(line: ParsedInvoiceLine, value: string) {
    if (readOnly) return;
    if (value === CUSTOM_INTERPRETED_UNIT) {
      updateLine(line.id, { interpretedUnit: reviewedUnitLabel(line) || "" });
      return;
    }
    updateLine(line.id, { interpretedUnit: value });
  }

  return (
    <>
      {importedEventId && (
        <div className="invoice-import-notice invoice-import-notice-success">
          <p>Imported · This invoice is locked because it has already created an Event.</p>
          <Link className="primary-button" to={`/events/${importedEventId}`}>Open Event</Link>
        </div>
      )}

      {actionError && !importedEventId && (
        <div className="invoice-import-notice invoice-import-notice-warning">
          <p>{actionError}</p>
          {conflictEventId && (
            <Link className="primary-button" to={`/events/${conflictEventId}`}>Open Event</Link>
          )}
        </div>
      )}

      {draft.parseWarnings.length > 0 && (
        <div className="invoice-import-warnings">
          {draft.parseWarnings.map((warning) => <p key={warning}>{warning}</p>)}
        </div>
      )}

      {validationErrors.length > 0 && !importedEventId && (
        <div className="invoice-import-notice invoice-import-notice-warning">
          {validationErrors.map((error) => <p key={error}>{error}</p>)}
        </div>
      )}

      <div className="invoice-import-grid">
        <label className="field-label">Client name<input className="field-input" disabled={readOnly} value={draft.clientName ?? ""} onChange={(event) => updateDraftField("clientName", event.target.value)} /></label>
        <label className="field-label">Email<input className="field-input" disabled={readOnly} value={draft.clientEmail ?? ""} onChange={(event) => updateDraftField("clientEmail", event.target.value)} /></label>
        <label className="field-label">Phone<input className="field-input" disabled={readOnly} value={draft.clientPhone ?? ""} onChange={(event) => updateDraftField("clientPhone", event.target.value)} /></label>
        <label className="field-label">Invoice #<input className="field-input" disabled={readOnly} value={draft.invoiceNumber ?? ""} onChange={(event) => updateDraftField("invoiceNumber", event.target.value)} /></label>
        <label className="field-label">Issue date<input className="field-input" disabled={readOnly} value={draft.invoiceIssueDate ?? ""} onChange={(event) => updateDraftField("invoiceIssueDate", event.target.value)} /></label>
        <label className="field-label">Project / Event name<input className="field-input" disabled={readOnly} value={draft.projectName ?? ""} onChange={(event) => updateDraftField("projectName", event.target.value)} /></label>
        <label className="field-label">Event type<input className="field-input" disabled={readOnly} value={draft.eventType ?? ""} onChange={(event) => updateDraftField("eventType", event.target.value)} /></label>
        <label className="field-label">Event date<input className="field-input" disabled={readOnly} value={draft.eventDate ?? ""} onChange={(event) => updateDraftField("eventDate", event.target.value)} /></label>
        <label className="field-label">Venue / location<input className="field-input" disabled={readOnly} value={draft.venue ?? ""} onChange={(event) => updateDraftField("venue", event.target.value)} /></label>
        <label className="field-label">Subtotal<input className="field-input" disabled={readOnly} value={draft.subtotal ?? ""} onChange={(event) => updateDraftField("subtotal", event.target.value)} /></label>
        <label className="field-label">Invoice total<input className="field-input" disabled={readOnly} value={draft.invoiceTotal ?? ""} onChange={(event) => updateDraftField("invoiceTotal", event.target.value)} /></label>
      </div>

      {draft.paymentSchedule.length > 0 && (
        <div className="invoice-import-section">
          <h2>Payment schedule</h2>
          <div className="invoice-payment-list">
            {draft.paymentSchedule.map((entry) => (
              <div key={entry.rawLine} className="invoice-payment-row">
                <strong>{entry.amount != null ? `$${entry.amount.toFixed(2)}` : "—"}</strong>
                <span>{entry.dueDate ?? "—"}</span>
                <span>{entry.status ?? "—"}</span>
                <small>{entry.rawLine}</small>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="invoice-import-section invoice-import-lines">
        <div className="section-heading small">
          <div>
            <h2>Line items</h2>
            <p>{draft.lines.length} lines{reviewCount > 0 ? ` · ${reviewCount} need review` : ""}</p>
          </div>
        </div>
        <div className="invoice-line-table">
          {draft.lines.map((line) => {
            const selectedUnit = unitSelectValue(line);
            const usingCustomUnit = selectedUnit === CUSTOM_INTERPRETED_UNIT;
            return (
              <div key={line.id} className={`invoice-line-row${line.needsReview ? " needs-review" : ""}`}>
                {line.needsReview && (
                  <span className="invoice-line-review-badge">Needs review</span>
                )}
                <div className="invoice-line-col invoice-line-col-item">
                  <strong className="invoice-line-name">{line.name}</strong>
                  {(line.rawNotes || line.description) && (
                    <p className="invoice-line-desc">{line.rawNotes || line.description}</p>
                  )}
                  <p className="invoice-line-source">{line.rawLine}</p>
                </div>
                <div className="invoice-line-col invoice-line-col-qty">
                  <div className="invoice-line-detected-review">
                    <span className="invoice-line-meta-label">Detected qty</span>
                    <span className="invoice-line-qty-value">{line.rawQuantity}</span>
                  </div>
                  {showReviewedQuantityField(line) && (
                    <label className="field-label">
                      Reviewed qty
                      <input
                        className="field-input"
                        type="number"
                        min={0}
                        step={1}
                        disabled={readOnly}
                        value={line.interpretedQuantity ?? line.rawQuantity}
                        onChange={(event) => {
                          const parsed = Number(event.target.value);
                          updateLine(line.id, {
                            interpretedQuantity: Number.isNaN(parsed) ? undefined : parsed,
                          });
                        }}
                      />
                    </label>
                  )}
                  <div className="invoice-line-detected-review">
                    <span className="invoice-line-meta-label">Detected unit</span>
                    <span className="invoice-line-detected-value">{detectedUnitLabel(line)}</span>
                  </div>
                  <label className="field-label">
                    Reviewed unit
                    <select
                      className="field-select"
                      disabled={readOnly}
                      value={selectedUnit}
                      onChange={(event) => handleUnitSelect(line, event.target.value)}
                    >
                      <option value="">Select unit…</option>
                      {INTERPRETED_UNIT_PRESETS.map((preset) => (
                        <option key={preset.value} value={preset.value}>{preset.label}</option>
                      ))}
                    </select>
                  </label>
                  {usingCustomUnit && (
                    <label className="field-label">
                      Custom unit
                      <input
                        className="field-input"
                        disabled={readOnly}
                        value={reviewedUnitLabel(line)}
                        placeholder="Enter unit meaning"
                        onChange={(event) => updateLine(line.id, { interpretedUnit: event.target.value })}
                      />
                    </label>
                  )}
                  {!usingCustomUnit && reviewedUnitLabel(line) && (
                    <p className="invoice-line-reviewed-value">Reviewed: {reviewedUnitLabel(line)}</p>
                  )}
                </div>
                <div className="invoice-line-col invoice-line-col-price">
                  <span className="invoice-line-unit-price">Unit ${line.unitPrice.toFixed(2)}</span>
                  <strong className="invoice-line-total">Total ${line.lineTotal.toFixed(2)}</strong>
                </div>
                <div className="invoice-line-col invoice-line-col-class">
                  <label className="field-label">
                    Classification
                    <select
                      className="field-select"
                      disabled={readOnly}
                      value={line.classification}
                      onChange={(event) => updateLine(line.id, {
                        classification: event.target.value as InvoiceLineClassification,
                      })}
                    >
                      {(Object.keys(invoiceLineClassificationLabels) as InvoiceLineClassification[]).map((key) => (
                        <option key={key} value={key}>{invoiceLineClassificationLabels[key]}</option>
                      ))}
                    </select>
                  </label>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {!importedEventId && !readOnly && !canCreateEvent && createBlockingReasons.length > 0 && (
        <div className="invoice-import-notice invoice-import-notice-muted">
          {createBlockingReasons.map((reason) => (
            <p key={reason}>{reason}</p>
          ))}
        </div>
      )}

      {!importedEventId && !readOnly && (
        <div className="invoice-import-actions">
          <button
            type="button"
            className="primary-button"
            disabled={!canCreateEvent}
            onClick={onCreateEvent}
          >
            {isCreating ? "Creating Event…" : "Create Event (reviewed)"}
          </button>
          <button
            type="button"
            className="secondary-button"
            disabled={!canMarkReady || markedReady || isMarkingReady || isCreating}
            onClick={onMarkReady}
          >
            {isMarkingReady
              ? "Marking ready…"
              : markedReady
                ? "Ready"
                : "Mark ready for import"}
          </button>
          <p className="helper-copy">
            {markedReady
              ? "Review complete. Create Event to save this invoice as a new Event."
              : "Compare the PDF, correct line items, then mark ready before creating the Event."}
          </p>
        </div>
      )}
    </>
  );
}
